'use client';

import { ChakraProvider, EnvironmentProvider } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from 'next-themes';
import { createContext, useContext, useMemo, useRef, useState } from 'react';
import root from 'react-shadow/emotion';
import { system as defaultSystem } from '../utils/chakra';

const ProviderContext = createContext(false);

/** @type {import('react').Context<import('react').RefObject<HTMLElement | null> | null>} */
export const PortalContainerContext = createContext(null);

export function Provider({ system = defaultSystem, shadow = true, ...props }) {
  const [host, setHost] = useState(null);
  const portalContainerRef = useRef(null);
  const cache = useMemo(() => {
    if (!host?.shadowRoot) {
      return null;
    }
    return createCache({
      key: 'root',
      container: host.shadowRoot,
    });
  }, [host]);

  const testEnv = process.env.NODE_ENV === 'test';
  if (testEnv || !shadow) {
    return (
      <ProviderContext.Provider value={true}>
        <PortalContainerContext.Provider value={portalContainerRef}>
          <ChakraProvider value={system}>
            <ThemeProvider {...props} />
            <div
              ref={portalContainerRef}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                zIndex: 'var(--chakra-z-index-popover)',
              }}
            />
          </ChakraProvider>
        </PortalContainerContext.Provider>
      </ProviderContext.Provider>
    );
  }
  return (
    <ProviderContext.Provider value={true}>
      <root.div
        ref={setHost}
        style={{
          outline: 'none',
          boxShadow: 'none',
          background: 'none',
        }}
        tabIndex={-1}
      >
        {host && cache && (
          <EnvironmentProvider value={() => host.shadowRoot ?? document}>
            <CacheProvider value={cache}>
              <ChakraProvider value={system}>
                <PortalContainerContext.Provider value={portalContainerRef}>
                  <ThemeProvider {...props} />
                  <div
                    ref={portalContainerRef}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: 0,
                      height: 0,
                      zIndex: 'var(--chakra-z-index-popover)',
                    }}
                  />
                </PortalContainerContext.Provider>
              </ChakraProvider>
            </CacheProvider>
          </EnvironmentProvider>
        )}
      </root.div>
    </ProviderContext.Provider>
  );
}

export function ConditionalProvider(props) {
  const hasProvider = useContext(ProviderContext);
  if (hasProvider) {
    return props.children;
  }
  return <Provider {...props} />;
}
